import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

/**
 * ProjectCompletionModal Component
 * 
 * Modal for final project completion with cost entry
 */
const ProjectCompletionModal = ({ 
  isOpen, 
  onClose, 
  onComplete, 
  project,
  completedSpansCount,
  totalSpansCount
}) => {
  const [formData, setFormData] = useState({
    actualStartDate: project?.planned_start_date || '',
    actualEndDate: new Date().toISOString().split('T')[0],
    actualMaterialCost: '',
    actualLaborCost: '',
    actualMobilizationCost: '',
    completionNotes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const allSpansCompleted = completedSpansCount === totalSpansCount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate costs
    const materialCost = parseFloat(formData.actualMaterialCost) || 0;
    const laborCost = parseFloat(formData.actualLaborCost) || 0;
    const mobilizationCost = parseFloat(formData.actualMobilizationCost) || 0;
    const totalCost = materialCost + laborCost + mobilizationCost;

    if (totalCost <= 0) {
      setError('Please enter valid cost values. Total cost must be greater than zero.');
      return;
    }

    setSubmitting(true);
    try {
      await onComplete({
        actualStartDate: formData.actualStartDate,
        actualEndDate: formData.actualEndDate,
        actualMaterialCost: materialCost,
        actualLaborCost: laborCost,
        actualMobilizationCost: mobilizationCost,
        completionNotes: formData.completionNotes
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to complete project');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white flex items-center">
            <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
            Complete Repair Project
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Validation Warning */}
          {!allSpansCompleted && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 mt-0.5 mr-3" />
                <div>
                  <h4 className="text-sm font-semibold text-yellow-800">Incomplete Spans</h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    {completedSpansCount} of {totalSpansCount} spans completed. 
                    When you complete the project, all remaining spans will automatically be marked as complete.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Project Summary */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Project Summary</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Project Name</p>
                <p className="font-medium text-gray-900">{project.project_name}</p>
              </div>
              <div>
                <p className="text-gray-600">Bridge</p>
                <p className="font-medium text-gray-900">{project.bridge_name}</p>
              </div>
              <div>
                <p className="text-gray-600">Total Spans</p>
                <p className="font-medium text-gray-900">{totalSpansCount}</p>
              </div>
              <div>
                <p className="text-gray-600">Estimated Cost</p>
                <p className="font-medium text-gray-900">
                  {new Intl.NumberFormat('en-CA', {
                    style: 'currency',
                    currency: 'CAD'
                  }).format(project.estimated_cost || 0)}
                </p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-4">
            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Actual Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.actualStartDate}
                  onChange={(e) => handleChange('actualStartDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Actual End Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.actualEndDate}
                  onChange={(e) => handleChange('actualEndDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>
            </div>

            {/* Cost Breakdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Actual Costs (CAD) <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-600">Material Costs</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.actualMaterialCost}
                    onChange={(e) => handleChange('actualMaterialCost', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Labor Costs</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.actualLaborCost}
                    onChange={(e) => handleChange('actualLaborCost', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Mobilization Costs</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.actualMobilizationCost}
                    onChange={(e) => handleChange('actualMobilizationCost', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="0.00"
                  />
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Total Cost:</span>
                    <span className="text-lg font-bold text-gray-900">
                      {new Intl.NumberFormat('en-CA', {
                        style: 'currency',
                        currency: 'CAD'
                      }).format(
                        (parseFloat(formData.actualMaterialCost) || 0) +
                        (parseFloat(formData.actualLaborCost) || 0) +
                        (parseFloat(formData.actualMobilizationCost) || 0)
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Completion Notes
              </label>
              <textarea
                value={formData.completionNotes}
                onChange={(e) => handleChange('completionNotes', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Any additional notes about project completion..."
              />
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {submitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Completing...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                  Complete Project
                </>
              )}
            </button>
          </div>

          {/* Warning Note */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
            <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
            <strong>Note:</strong> Completing this project will update the baseline data for all spans 
            (last painted year, repair process) to ensure accurate future simulations.
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectCompletionModal;


